/* eslint-disable @typescript-eslint/no-explicit-any */
import { cloneDeep } from 'lodash'
import { getItemToProcess } from '../../app/util'
import { isSyncSuspended } from '../../app/sync/suspend'
import { SQL, AppTableName, APP_TABLE_NAMES } from '../Sqlite'
import { isApp } from '@/index'
import { Capacitor } from '@capacitor/core'
import {
  generateTableSQLFromObject,
  prepareSQLiteValue,
  toStoredSQLiteValue,
  UNSURE_SQLITE_VALUE,
  type SQLiteAffinity,
} from '../Sqlite/schema'
import { ReturnDataType, SQLiteColumnType } from './types'
import { ShapeTemplateType } from '../../app/data/type'
import { InspectionType, VoiceFile } from '../../app/data/types'
import { SQLiteEventBus } from '../sqliteEventBus'

// 모듈 레벨 변수
let database: any = null
let initPromise: Promise<void> | null = null
const storeKeys: string[] = [
  'tasks',
  'facility_photos',
  'defects',
  'defect_photos',
  'device_event_log',
  'docs',
  'floorplans',
  'voice_files',
  'quality_checklist_zones',
  'quality_checklist_items',
  'quality_inspections',
  'quality_inspection_results',
  'quality_result_photos',
  'quality_partners',
]

// 테이블별 JSON 파싱이 필요한 필드 (나머지는 parseValueFromDB 스킵)
// 대량 테이블(defects, defect_photos)만 선택적 파싱 적용
// 소량 테이블(tasks, docs 등)은 목록에 없으므로 전체 파싱 (안전)
const JSON_COLUMNS: Record<string, Set<string>> = {
  defects: new Set(['shape_data', 'fields_updated_at', 'test_measure_data']),
  defect_photos: new Set(['fields_updated_at', 'drawing']),
}

// PRAGMA table_info 결과 캐시 (테이블별 컬럼 목록)
export const columnCache = new Map<string, string[]>()
// 같은 PRAGMA 결과의 컬럼별 affinity (updateSqliteOnly 가 저장된 행 모양을 계산할 때 사용)
// 이벤트에 저장 행을 실어 보내는 최대 건수 (사용자 저장·일괄편집 규모)
const EMIT_ROWS_MAX = 50
export const columnAffinityCache = new Map<string, Map<string, SQLiteAffinity>>()
export function invalidateColumnCache(storeKey?: string): void {
  if (storeKey) {
    columnCache.delete(storeKey)
    columnAffinityCache.delete(storeKey)
  } else {
    columnCache.clear()
    columnAffinityCache.clear()
  }
}

/**
 * INSERT OR REPLACE 로 쓴 값이 SELECT * 로 어떻게 돌아올지 계산한다(빠진 컬럼은 NULL — 기본값 있는 컬럼 없음).
 * @param prepared insertColumns 순서의 prepareSQLiteValue 결과
 * @returns 확신할 수 없는 값이 하나라도 있으면 null
 */
function toStoredRow(
  prepared: unknown[],
  insertColumns: string[],
  affinities: Map<string, SQLiteAffinity>,
): Record<string, unknown> | null {
  const row: Record<string, unknown> = {}
  for (const [column, affinity] of affinities) {
    const index = insertColumns.indexOf(column)
    const stored = index < 0 ? null : toStoredSQLiteValue(prepared[index], affinity)
    if (stored === UNSURE_SQLITE_VALUE) return null
    row[column] = stored
  }
  return row
}

/**
 * 비동기 초기화 함수
 * @returns {Promise<void>}
 */
async function init(): Promise<void> {
  if (database) return // 이미 초기화됨
  if (!isApp()) return // 웹에서는 SQLite 사용하지 않음
  database = await SQL.getDatabase()
}

/**
 * SQLite 데이터베이스 초기화 및 인스턴스 보장
 * @returns {Promise<void>}
 */
async function ensureInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = init()
  }
  await initPromise
}

const SQLite = {
  /**
   * storeKeys 배열 조회
   * @returns {string[]}
   */
  getStoreKeys: (): string[] => {
    return storeKeys
  },

  /**
   * 생성된 테이블 목록 조회
   * @returns {Set<string>}
   */
  getCreatedTables: (): Set<string> => {
    return SQL.createdTables
  },

  /**
   * 데이터베이스 인스턴스 가져오기
   * @returns {Promise<any>}
   */
  getDatabase: async (): Promise<any> => {
    await ensureInitialized()
    return database
  },

  getCurrentTables: (): string[] => {
    return [
      'defects',
      'facility_photos',
      'defect_photos',
      'device_event_log',
      'docs',
      'tasks',
      'floorplans',
      'voice_files',
      'quality_checklist_zones',
      'quality_checklist_items',
      'quality_inspections',
      'quality_inspection_results',
      'quality_result_photos',
      'quality_partners',
    ]
  },

  getCurrentDefaultTableData: (table: string): ReturnDataType[] => {
    //data/types.ts 참고
    switch (table) {
      case 'defects':
        return [
          {
            id: 0,
            task_id: 0,
            facility_id: 0,
            floorplan_id: 0,
            company_id: 0,
            created_user_id: 0,
            previous_defect_id: 0,
            parent_defect_id: 0,
            shape_type: '',
            shape_data: {},
            color: '',
            memo: '',
            structural_component: '',
            classification: '',
            progressing: 0,
            width: '',
            length: '',
            area: '',
            count: '',
            is_structural: 0,
            repair_plan: '',
            cause_estimate: '',
            template: 'custom-RC' as ShapeTemplateType,
            previous_balloon_number: '',
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
            hasPhoto: false,
            mainPhotoUrl: '',
            prevPhotoUrl: '',
            indexing: '',
            defect_photos: [],
            inspection_type: 'STATUS_OUTSIDE' as InspectionType,
            test_measure_data: null,
            is_current: 0,
          },
        ]
      case 'facility_photos':
        return [
          {
            id: 0,
            facility_id: 0,
            task_id: 0,
            company_id: 0,
            created_user_id: 0,
            memo: '',
            url: '',
            drawing: null,
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            sort_key: '',
            modifiedFromLocal: false,
          },
        ]
      case 'defect_photos':
        return [
          {
            id: 0,
            defect_id: 0,
            task_id: 0,
            company_id: 0,
            facility_id: 0,
            floorplan_id: 0,
            created_user_id: 0,
            url: '',
            is_previous: false,
            is_main: false,
            drawing: null,
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localShapeId: '',
            localStringId: '',
            modifiedFromLocal: false,
            inspection_type: 'STATUS_OUTSIDE' as InspectionType,
          },
        ]
      case 'device_event_log':
        return [
          {
            id: 0,
            task_id: 0,
            related_id: 0,
            event_type: '',
            environment: '',
            company_id: 0,
            created_user_id: 0,
            author: '',
            old_value: '',
            new_value: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localRelatedId: '',
            deleted: 0,
            modifiedFromLocal: false,
          },
        ]
      case 'docs':
        return [
          {
            id: 0,
            template_id: 0,
            company_id: 0,
            task_id: 0,
            facility_id: 0,
            page_no: 0,
            items: '[]', // JSON
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            brush_data: [],
            localStringId: '',
            modifiedFromLocal: false,
            url: '',
          },
        ]
      case 'tasks':
        return []
      case 'floorplans':
        return [
          {
            id: 0,
            facility_id: 0,
            task_id: 0,
            company_id: 0,
            created_user_id: 0,
            name: '',
            sort_key: '',
            file_url: '',
            origin_file_url: '',
            rotate: 0,
            tile_config: {
              bounds: { width: 0, height: 0 },
              levels: [],
              tileSize: 0,
              floorplanId: 0,
              createdAt: 0,
            },
            tile_base_url: '',
            is_tiled: false,
            width: 0,
            height: 0,
            balloon_scale: 0,
            balloon_font_scale: 0,
            connected_line_thickness_scale: 0,
            point_shape_scale: 0,
            area_shape_border_thickness_scale: 0,
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'voice_files':
        return [
          {
            id: 0,
            task_id: 0,
            facility_id: 0,
            floorplan_id: 0,
            defect_id: 0,
            template_id: 0,
            record_item_key: '',
            company_id: 0,
            created_user_id: 0,
            url: '',
            name: '',
            stt_text: '',
            summary_text: '',
            duration_ms: 0,
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
            fileName: '',
          } as VoiceFile,
        ]
      case 'quality_checklist_zones':
        return [
          {
            id: 0,
            company_id: 0,
            set_id: 0,
            task_id: 0,
            source_zone_id: 0,
            created_user_id: 0,
            name: '',
            location_type: '',
            locations: [],
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'quality_checklist_items':
        return [
          {
            id: 0,
            company_id: 0,
            set_id: 0,
            zone_id: 0,
            task_id: 0,
            source_item_id: 0,
            created_user_id: 0,
            trade: '',
            item_no: '',
            question: '',
            defect_content: '',
            locations: [],
            locations_uncertain: [],
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'quality_inspections':
        return [
          {
            id: 0,
            task_id: 0,
            company_id: 0,
            zone_id: 0,
            created_user_id: 0,
            reviewed_user_id: 0,
            round: 0,
            name: '',
            building: '',
            unit: '',
            section: '',
            inspector_name: '',
            inspected_date: '',
            status: 'DRAFT',
            memo: '',
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'quality_inspection_results':
        return [
          {
            id: 0,
            inspection_id: 0,
            item_id: 0,
            task_id: 0,
            company_id: 0,
            created_user_id: 0,
            location: '',
            location_detail: {},
            result: 'NA',
            note: '',
            note_2: '',
            note_3: '',
            set_count: 1,
            primary_set: 1,
            custom_defect_content: '',
            floorplan_id: 0,
            shape_type: '',
            shape_data: {},
            balloon_number: 0,
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'quality_result_photos':
        return [
          {
            id: 0,
            result_id: 0,
            inspection_id: 0,
            task_id: 0,
            company_id: 0,
            created_user_id: 0,
            url: '',
            photo_type: 'NEAR',
            set_no: 1,
            caption: '',
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localResultId: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      case 'quality_partners':
        return [
          {
            id: 0,
            task_id: 0,
            company_id: 0,
            created_user_id: 0,
            section: '',
            trade: '',
            partner_name: '',
            manager_name: '',
            sort_key: '',
            fields_updated_at: {},
            created_at: '',
            updated_at: '',
            deleted_at: '',
            localStringId: '',
            modifiedFromLocal: false,
          },
        ]
      default:
        return []
    }
  },

  /**
   * 업데이트가 필요한 데이터와 컬럼을 가져옴
   * @param {ReturnDataType[]} datas - 업데이트할 데이터 배열
   * @param {string} storeKey - 테이블 이름
   * @returns {Promise<{ itemsToProcess: { item: ReturnDataType; itemId: string }[]; columns: string[] }>}
   */
  /**
   * 병합 전 로컬 행을 id별로 고정한다(병합이 로컬 객체를 제자리에서 바꿔도 비교 기준이 흔들리지 않게 값을 미리 직렬화).
   * @param rows getLocalData 결과(병합 입력)
   * @returns id → 컬럼별 prepareSQLiteValue 결과
   */
  snapshotRowsById: async (
    rows: Record<string, unknown>[],
  ): Promise<Map<string, Map<string, unknown>>> => {
    const snapshot = new Map<string, Map<string, unknown>>()
    let count = 0
    for (const row of rows) {
      // 직렬화가 메인 스레드를 오래 잡지 않도록 양보
      if (++count % 200 === 0) await new Promise(resolve => setTimeout(resolve, 0))
      if (row.id === undefined || row.id === null) continue
      const values = new Map<string, unknown>()
      for (const [key, value] of Object.entries(row)) values.set(key, prepareSQLiteValue(value))
      snapshot.set(String(row.id), values)
    }
    return snapshot
  },

  /**
   * 병합 저장(updateSqliteOnly allColumns)에서 DB 행을 바꾸지 않을 항목을 뺀다.
   * 테이블 전 컬럼을 저장될 값 기준(affinity 정규화)으로 병합 전 스냅샷과 비교하고, 하나라도 다르거나 확신할 수 없으면 쓴다.
   * 컬럼 affinity를 모르면 전부 쓴다.
   * @param itemsToProcess getNeedUpdateItems 결과
   * @param storeKey 테이블 이름
   * @param taskId 저장에 쓰는 task_id(updateSqliteOnly에 넘기는 값과 같아야 함)
   * @param snapshot snapshotRowsById 결과
   * @returns 써야 할 항목과 건너뛴 건수
   */
  skipUnchangedRows: async <T extends { item: ReturnDataType; itemId: string }>(
    itemsToProcess: T[],
    storeKey: string,
    taskId: string,
    snapshot: Map<string, Map<string, unknown>>,
  ): Promise<{ items: T[]; skipped: number }> => {
    const affinities = columnAffinityCache.get(storeKey)
    const columns = columnCache.get(storeKey)
    if (!affinities || !columns || columns.some(c => !affinities.has(c))) {
      return { items: itemsToProcess, skipped: 0 }
    }
    const isTasksTable = storeKey === 'tasks' || !taskId
    const items: T[] = []
    let compared = 0
    for (const entry of itemsToProcess) {
      // 직렬화 비교가 메인 스레드를 오래 잡지 않도록 양보
      if (++compared % 200 === 0) await new Promise(resolve => setTimeout(resolve, 0))
      const local = snapshot.get(String(entry.itemId))
      const same =
        local !== undefined &&
        columns.every(column => {
          const affinity = affinities.get(column)!
          const written =
            column === 'id'
              ? prepareSQLiteValue(entry.itemId)
              : column === 'task_id' && !isTasksTable
                ? prepareSQLiteValue(taskId)
                : prepareSQLiteValue((entry.item as unknown as Record<string, unknown>)[column])
          const next = toStoredSQLiteValue(written, affinity)
          const prev = toStoredSQLiteValue(local.get(column) ?? null, affinity)
          return next !== UNSURE_SQLITE_VALUE && prev !== UNSURE_SQLITE_VALUE && next === prev
        })
      if (!same) items.push(entry)
    }
    return { items, skipped: itemsToProcess.length - items.length }
  },

  getNeedUpdateItems: async (
    datas: ReturnDataType[],
    storeKey: string,
  ): Promise<{ itemsToProcess: { item: ReturnDataType; itemId: string }[]; columns: string[] }> => {
    return await getItemToProcess(datas, storeKey)
  },

  /**
   * 업데이트가 필요한 데이터를 SQLite에 업데이트
   * getNeedUpdateItems이 선행되어야함
   * @param {{ item: ReturnDataType; itemId: string }[]} itemsToProcess - 업데이트할 아이템 배열
   * @param {string[]} columns - 컬럼 목록
   * @param {string} taskId - 작업 ID
   * @param {string} storeKey - 테이블 이름
   * @param {boolean} skipEmit - 이벤트 발행 생략
   * @param {boolean} allColumns - 첫 행 키 대신 테이블 전 컬럼으로 쓴다(병합 저장 — 행마다 키가 달라도 로컬 전용 컬럼이 NULL로 지워지지 않게)
   * @returns {Promise<void>}
   */
  updateSqliteOnly: async (
    itemsToProcess: { item: ReturnDataType; itemId: string }[],
    columns: string[],
    taskId: string,
    storeKey: string,
    skipEmit: boolean = false,
    allColumns: boolean = false,
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return
    // 매뉴얼(사용 설명서) 데모 스냅샷이 열려 있는 동안(동기화 일시정지 상태)의 영속 차단:
    // 스냅샷 화면들의 initData가 데모 픽스처(과업 9xxx)를 실제 DB에 병합하는 것을 막는다.
    // 스냅샷 렌더는 스토어 메모리(.value) 대입으로 동작하므로 화면에는 영향 없음.
    if (isSyncSuspended()) return

    try {
      const db = await SQL.getDatabase()

      // tasks 테이블 여부 확인 (storeKey 기준으로 정확히 판단)
      // 과거에는 !taskId 만으로 판단했으나, tasks 테이블에 빈 string 아닌 currentTaskId가
      // 넘어오는 호출 경로(예: task.ts의 단건 detail INSERT)에서 isTasksTable=false로
      // 잘못 분기되어 INSERT 컬럼 구성과 itemId/taskId 변환이 어긋났다.
      // storeKey === 'tasks' 인 경우 항상 isTasksTable=true 로 강제한다.
      const isTasksTable = storeKey === 'tasks' || !taskId

      // 배치 처리를 위한 설정
      const insertColumns = ['id']
      if (!isTasksTable) {
        insertColumns.push('task_id')
      }

      // 유효한 컬럼 찾기 — 기본은 첫 행의 키, allColumns면 테이블 전 컬럼(행에 없는 키는 NULL)
      if (allColumns) {
        for (const column of columns) {
          if (!insertColumns.includes(column)) insertColumns.push(column)
        }
      } else {
        for (const [key] of Object.entries(itemsToProcess[0].item)) {
          if (key === 'id') continue
          if (columns.includes(key)) {
            insertColumns.push(key)
          }
        }
      }

      // 배치 크기: 컬럼 수 기반 동적 계산 (SQLite 변수 제한 999 안전 마진)
      const BATCH_SIZE = Math.min(200, Math.max(50, Math.floor(999 / insertColumns.length)))

      // 리스너가 SELECT 재조회 없이 쓸 수 있도록 저장될 행 모양을 함께 만든다.
      // 소량 갱신(사용자 저장)만 대상 — 대량 병합·확신할 수 없는 값이 있으면 리스너가 지금처럼 SELECT 한다.
      // boolean·number 바인딩은 네이티브가 정한다 — Android 만 검증됨. iOS 는 기기 검증 후 연다.
      const affinities =
        !skipEmit && itemsToProcess.length <= EMIT_ROWS_MAX && Capacitor.getPlatform() === 'android'
          ? columnAffinityCache.get(storeKey)
          : undefined
      let emitRows: Record<string, unknown>[] | undefined =
        affinities && insertColumns.every(c => affinities.has(c)) ? [] : undefined

      // executeSet: 네이티브 레벨에서 단일 트랜잭션으로 전체 배치 실행 (커밋 1회)
      const stmtSet: Array<{ statement: string; values: any[] }> = []
      for (let i = 0; i < itemsToProcess.length; i += BATCH_SIZE) {
        const batch = itemsToProcess.slice(i, i + BATCH_SIZE)
        const batchValuePlaceholders: string[] = []
        const batchValues: any[] = []

        for (const { item, itemId } of batch) {
          const rowStart = batchValues.length
          const valuePlaceholders = ['?']
          // iOS Capacitor SQLite native bridge가 number 를 TEXT 컬럼에 "557.0" 형태로
          // 저장하던 문제를 차단하기 위해 id / task_id 도 prepareSQLiteValue 로 정규화.
          // 다른 컬럼은 이미 아래 for 루프에서 정규화되고 있었다.
          batchValues.push(prepareSQLiteValue(itemId))

          if (!isTasksTable) {
            valuePlaceholders.push('?')
            batchValues.push(prepareSQLiteValue(taskId))
          }

          const startIndex = isTasksTable ? 1 : 2
          for (let j = startIndex; j < insertColumns.length; j++) {
            const key = insertColumns[j]
            valuePlaceholders.push('?')
            batchValues.push(
              prepareSQLiteValue((item as ReturnDataType)[key as keyof ReturnDataType]),
            )
          }
          batchValuePlaceholders.push(`(${valuePlaceholders.join(', ')})`)
          if (emitRows && affinities) {
            const row = toStoredRow(batchValues.slice(rowStart), insertColumns, affinities)
            if (row) emitRows.push(row)
            else emitRows = undefined
          }
        }

        stmtSet.push({
          statement: `INSERT OR REPLACE INTO ${storeKey} (${insertColumns.join(', ')}) VALUES ${batchValuePlaceholders.join(', ')}`,
          values: batchValues,
        })
      }

      await db.executeSet(stmtSet)

      // DB 업데이트 성공 직후 이벤트 발행
      if (!skipEmit && APP_TABLE_NAMES.includes(storeKey as any)) {
        const updatedIds = itemsToProcess
          .map(
            ({ item }) =>
              (item as ReturnDataType & { localStringId?: string }).localStringId ||
              item.id?.toString() ||
              '',
          )
          .filter(id => id !== '')

        await SQLiteEventBus.getInstance().emitUpdate(
          storeKey as AppTableName,
          'update',
          updatedIds,
          emitRows,
        )
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error(`updateSqliteOnly ERROR ${storeKey}: ${msg}`)
      throw e
    }
  },

  /**
   * 테이블이 존재하는지 확인하고 필요시 생성 또는 업데이트합니다.
   *
   * @param {string} tableName - 확인할 테이블 이름
   * @param {ReturnDataType[]} [data] - 테이블 생성/업데이트 시 참고할 데이터
   * @returns {Promise<boolean>} 테이블 존재 여부 (true: 존재함, false: 새로 생성함)
   *
   * @description
   * 테이블 존재 여부를 확인하고 다음 작업을 수행합니다:
   * 1. 테이블이 없으면: data를 기반으로 새 테이블 생성
   * 2. 테이블이 있고 data가 제공되면: data의 컬럼과 기존 테이블 컬럼 비교하여 누락된 컬럼 추가
   * 3. 테이블이 있고 data가 없으면: 기존 테이블 그대로 사용
   */
  checkTableExists: async (tableName: string, data?: ReturnDataType[]): Promise<boolean> => {
    const db = await SQL.getDatabase()
    const result = await db.query(`PRAGMA table_info(${tableName})`)

    // 테이블이 존재하지 않는 경우: 새로 생성
    if (result.values?.length === 0) {
      const createTableSQL = await SQLite.getDynamicTableSql(
        tableName,
        SQLite.getCurrentDefaultTableData(tableName),
      )
      if (createTableSQL && createTableSQL.trim()) {
        await db.execute(createTableSQL)
        SQL.createdTables.add(tableName)
        return false // 새로 생성함
      }
    }
    // 테이블이 존재하는 경우: 컬럼 비교 및 누락된 컬럼 추가
    // (서버 데이터 + 기본 스키마 합집합 - 업그레이드 설치본은 서버 응답 없이도(오프라인) 새 컬럼이 생겨야
    //  로컬 쓰기가 유실되지 않음. 예: quality_inspection_results.custom_defect_content)
    else if (result.values?.length > 0) {
      const existingColumns = result.values.map((col: any) => col.name)
      const defaultSample = SQLite.getCurrentDefaultTableData(tableName)[0] ?? {}
      const sample = { ...defaultSample, ...(data?.[0] ?? {}) } as ReturnDataType
      if (Object.keys(sample).length > 0) {
        await SQLite.addMissingColumns(tableName, [sample], existingColumns)
      }
      return true // 이미 존재함
    }

    return result.values?.length > 0
  },

  /**
   * 로컬 데이터베이스에서 값을 파싱합니다.
   * iOS Capacitor SQLite의 number → TEXT stringify 시 "15522.0" 형태가 되는 문제를
   * READ 시점에 함께 normalize 한다(쓰기 fix만으로는 기존 저장 데이터가 복구되지 않음).
   * @param {any} value - 파싱할 값
   * @returns {any} 파싱된 값
   */
  parseValueFromDB: (value: any): any => {
    if (typeof value !== 'string') return value

    // JSON 형식으로 보이면 파싱 시도
    if (
      (value.startsWith('{') || value.startsWith('[')) &&
      (value.endsWith('}') || value.endsWith(']'))
    ) {
      try {
        return JSON.parse(value)
      } catch {
        // JSON 파싱 실패 시 fallthrough하여 ".0" normalize 시도
      }
    }

    // iOS native가 정수 number를 TEXT 컬럼에 "15522.0"으로 stringify한 케이스를 복원
    // "0.5" 같은 진짜 fractional은 매칭되지 않으므로 그대로 보존
    if (/^-?\d+\.0$/.test(value)) {
      return value.slice(0, -2)
    }
    return value
  },

  /**
   * 데이터베이스 행을 파싱합니다.
   * @param {any} row - 파싱할 행
   * @param {string} tableName - 테이블 이름 (제공 시 JSON 필드만 선택 파싱)
   * @returns {any} 파싱된 행
   */
  parseRow: (row: any, tableName?: string): any => {
    const jsonFields = tableName ? JSON_COLUMNS[tableName] : null
    const parsedRow: any = {}
    for (const [field, value] of Object.entries(row)) {
      if (jsonFields && !jsonFields.has(field)) {
        // JSON 컬럼이 아니면 JSON.parse는 스킵하지만, ".0" normalize는 모든 컬럼에 적용
        parsedRow[field] =
          typeof value === 'string' && /^-?\d+\.0$/.test(value) ? value.slice(0, -2) : value
      } else {
        parsedRow[field] = SQLite.parseValueFromDB(value)
      }
    }
    return parsedRow
  },

  /**
   * 로컬 데이터를 조회합니다.
   * @param {string} tableName - 테이블 이름
   * @returns {Promise<ReturnDataType[]>} 로컬 데이터 배열
   */
  getLocalData: async (
    tableName: string,
    whereClause: string = '',
    paramId?: string | null,
    excludeDeleted: boolean = false, // 새로운 옵션
  ): Promise<ReturnDataType[]> => {
    const db = await SQL.getDatabase()
    try {
      if (!SQL.createdTables.has(tableName)) {
        throw new Error('테이블이 존재하지 않습니다.')
      }

      let sql = `SELECT * FROM ${tableName}`
      const params: any[] = []
      const conditions: string[] = []

      // whereClause 조건 추가
      if (whereClause && paramId !== undefined && paramId !== null) {
        conditions.push(`${whereClause} = ?`)
        params.push(paramId)
      }

      // deleted_at 필터링 추가
      if (excludeDeleted) {
        conditions.push('(deleted_at IS NULL OR deleted_at = "")')
      }

      // WHERE 절 조합
      if (conditions.length > 0) {
        sql += ` WHERE ${conditions.join(' AND ')}`
      }

      const localData = await db.query(sql, params)
      const result = localData.values?.map((row: unknown) =>
        SQLite.parseRow(row, tableName),
      ) as ReturnDataType[]
      return result
    } catch (error) {
      console.error(`${tableName} 로컬 데이터 가져오기 실패`, error)
      return []
    }
  },

  /**
   * 서버 데이터에는 있지만 테이블에 없는 컬럼을 추가합니다.
   *
   * @param {string} tableName - 테이블 이름
   * @param {ReturnDataType[]} serverData - 서버에서 받은 데이터
   * @param {string[]} existingColumns - 테이블에 이미 존재하는 컬럼 목록
   * @returns {Promise<void>}
   *
   * @description
   * 서버 데이터의 첫 번째 항목을 샘플로 사용하여:
   * 1. 서버 데이터의 모든 키를 추출
   * 2. 기존 테이블에 없는 컬럼 찾기
   * 3. 값의 타입에 따라 적절한 SQLite 컬럼 타입 결정
   * 4. ALTER TABLE로 컬럼 추가
   */
  addMissingColumns: async (
    tableName: string,
    serverData: ReturnDataType[],
    existingColumns: string[],
  ): Promise<void> => {
    if (!serverData || serverData.length === 0) return

    const db = await SQL.getDatabase()
    const sampleData = serverData[0]

    // 서버 데이터에 있지만 기존 테이블에 없는 컬럼들 찾기
    const missingColumns = Object.keys(sampleData).filter(key => !existingColumns.includes(key))

    if (missingColumns.length === 0) return

    try {
      for (const columnName of missingColumns) {
        const sampleValue = (sampleData as any)[columnName]
        let columnType = 'TEXT' // 기본값

        // 값의 타입에 따라 컬럼 타입 결정
        if (typeof sampleValue === 'number') {
          columnType = Number.isInteger(sampleValue) ? 'INTEGER' : 'REAL'
        } else if (typeof sampleValue === 'boolean') {
          columnType = 'INTEGER' // SQLite에서는 BOOLEAN을 INTEGER로 저장
        } else if (sampleValue instanceof Date) {
          columnType = 'TIMESTAMP'
        }

        // 컬럼 추가 실행
        const alterSQL = `ALTER TABLE ${tableName} ADD COLUMN ${columnName} ${columnType}`
        await db.execute(alterSQL)
        console.info(`Added column ${columnName} (${columnType}) to table ${tableName}`)
      }
      // 컬럼 변경 시 캐시 무효화
      invalidateColumnCache(tableName)
    } catch (error) {
      console.error(`Error adding missing columns to ${tableName}:`, error)
      // 컬럼 추가 실패해도 계속 진행
    }
  },

  /**
   * 동적으로 테이블 생성 SQL을 생성합니다.
   * @param {string} tableName - 테이블 이름
   * @param {any} data - 테이블 구조를 정의할 데이터
   * @param {boolean} isAlone - task_id 컬럼 제외 여부
   * @returns {Promise<string | undefined>}
   */
  getDynamicTableSql: (
    tableName: string,
    data: any,
    isAlone: boolean = false,
  ): Promise<string | undefined> => {
    return new Promise(resolve => {
      let createTableSQL
      const options = {
        idField: 'id',
        idType: 'TEXT' as SQLiteColumnType,
        includeDeletedField: false,
      }
      try {
        if (Array.isArray(data)) {
          // 배열인 경우
          if (data.length > 0) {
            const tempData = cloneDeep(data[0])
            if (!isAlone) {
              tempData.task_id = 0
            }
            createTableSQL = generateTableSQLFromObject(tableName, tempData, options)
          }
        } else {
          // 단일 객체인 경우
          const tempData = cloneDeep(data)
          if (!isAlone) {
            tempData.task_id = 0
          }
          createTableSQL = generateTableSQLFromObject(tableName, tempData, options)
        }
        resolve(createTableSQL)
      } catch (error) {
        console.error(`Error creating dynamic table ${tableName}:`, error)
        resolve(undefined)
      }
    })
  },
}

export default SQLite
